import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Form, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import { format } from 'date-fns'
import { IFilterSelectorOpts, IFilterSelectorOpts_Selected, PublisherOrCampaign } from '../home/dashboard-content/types'
import FiltersGrid from '../_filters-grid/filters-grid'
import { ICustomAlert } from '../alerts/custom-alerts/custom-alerts'
import { useAuth } from '../../../auth/auth-context'
import DateRangeSelector from '../home/dashboard-content/date-range-selector'
import { getTimeZoneOptions, resolveScheduledReportTimeZone } from './report-timezones'
import {
    getPerformanceLabels,
    inferPerformanceGranularity,
    PerformanceGranularity,
} from '../../../report-performance'
import { ReportSectionId, ReportUserKind } from '../../../report-sections'
import AdditionalReportPages from './additional-report-pages'

type IDownloadReportModalProps = {
    show: boolean
    onClose: () => void
    onDownload: (
        fromDate: string,
        toDate: string,
        filters: IFilterSelectorOpts_Selected,
        reportTitle: string,
        reportType: string,
        timeZone: string,
        selectedAlertIds?: string[],
        reportSections?: ReportSectionId[],
        performanceGranularity?: PerformanceGranularity
    ) => Promise<void>
    campaigns: PublisherOrCampaign[]
    publishers: PublisherOrCampaign[]
    channels: PublisherOrCampaign[]
    networks: string[]
    countries: string[]
    alerts: ICustomAlert[]
}

function isSameItem(a: any, b: any): boolean {
    if (typeof a === 'string' && typeof b === 'string') return a === b
    return a.id === b.id && a.network === b.network && a.country === b.country
}

function isAllSelected<T>(
    selected: T[],
    all: T[],
    compareFn: (a: T, b: T) => boolean
): boolean {
    if (selected.length !== all.length) return false
    return all.every(a => selected.some(b => compareFn(a, b)))
}

const DownloadReportModal: React.FC<IDownloadReportModalProps> = ({
    show, onClose, onDownload, campaigns, publishers, channels, networks, countries, alerts
}) => {
    const {user} = useAuth()
    const feedAlerts = user?.kind === 'publisher' && (user as any)?.feed?.disabled ? [] : [{id:'feed', name: 'Feed alerts'}]
    const allAlerts:{id:string, name:string}[] = user?.kind === 'publisher'
        ? [...feedAlerts, ...alerts]
        : [{id:'duplicate', name: 'Duplicate conversions'}, {id:'feed', name: 'Feed alerts'}, ...alerts] 
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [reportTitle, setReportTitle] = useState('')
    const [timeZone, setTimeZone] = useState(() => resolveScheduledReportTimeZone(undefined, true))
    const [isDownloading, setIsDownloading] = useState(false)
    const timeZoneOptions = useMemo(() => getTimeZoneOptions(), [])

    const [reportType, setReportType] = useState<'performance' | 'alert'>('performance')
    const [selectedAlertIds, setSelectedAlertIds] = useState<string[]>([]) // 🔹 NEW STATE
    const [reportSections, setReportSections] = useState<ReportSectionId[]>([])
    const performanceGranularity = inferPerformanceGranularity(fromDate, toDate)
    const performanceLabels = getPerformanceLabels(performanceGranularity)
    const userKind = (user?.kind === 'publisher' ? 'publisher' : 'advertiser') as ReportUserKind

    const [filters, setFilters] = useState<IFilterSelectorOpts>({
        countries: countries || [],
        networks: networks || [],
        publishers: publishers || [],
        campaigns: campaigns || [],
        channels: channels || []
    })

    useEffect(() => {
        setFilters({ countries, networks, publishers, campaigns, channels })
        setTimeZone(resolveScheduledReportTimeZone(undefined, true))
        setReportSections([])
    }, [campaigns, channels, countries, networks, publishers, show])

    function areAllItemsSelected(current: any[], all: any[]) {
        return current.length === all.length &&
            all.every(item =>
                current.some(i => isSameItem(i, item))
            )
    }

    const handleSelectAll = (
        filterKey: keyof typeof filters,
        options: any[]
    ) => {
        setFilters((prev) => ({
            ...prev,
            [filterKey]: areAllItemsSelected(prev[filterKey], options)
                ? []
                : [...options],
        }))
    }

    const handleDownload = async () => {
        if (!fromDate || !toDate) {
            alert('Please select both a from-date and a to-date.')
            return
        }

        if (reportType === 'alert' && selectedAlertIds.length === 0) {
            alert('Please select at least one alert.')
            return
        }

        setIsDownloading(true)

        const newFilters: IFilterSelectorOpts_Selected = reportType === 'performance' ? {
            campaigns: isAllSelected(filters.campaigns, campaigns, isSameItem) ? true : filters.campaigns,
            countries: isAllSelected(filters.countries, countries, (a, b) => a === b) ? true : filters.countries,
            networks: isAllSelected(filters.networks, networks, (a, b) => a === b) ? true : filters.networks,
            publishers: isAllSelected(filters.publishers, publishers, isSameItem) ? true : filters.publishers,
            channels: isAllSelected(filters.channels, channels, isSameItem) ? true : filters.channels
        } : {
            campaigns: true,
            countries: true,
            networks: true,
            publishers: true,
            channels: true
        }

        try {
            await onDownload(fromDate, toDate, newFilters, reportTitle, reportType, timeZone, selectedAlertIds, reportSections, performanceGranularity)
        } catch (error) {
            console.error('Download failed:', error)
            alert('Failed to download the report. Please try again.')
        } finally {
            setIsDownloading(false)
        }
    }

    const handleAlertToggle = (id: string) => {
        setSelectedAlertIds(prev =>
            prev.includes(id) ? prev.filter(aid => aid !== id) : [...prev, id]
        )
    }

    return (
        <Modal show={show} onHide={onClose} backdrop="static" keyboard={false} size='lg' className="report-download-modal">
            <Modal.Header closeButton>
                <Modal.Title>Download report</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group controlId="reportType">
                        <Form.Label>Report type</Form.Label>
                        <Form.Select
                            value={reportType}
                            onChange={(e) => {
                                const newType = e.target.value as 'performance' | 'alert'
                                setReportType(newType)
                                if (newType === 'alert') {
                                    setSelectedAlertIds(allAlerts.map(alert => alert.id))
                                } else {
                                    setSelectedAlertIds([])
                                }
                            }}
                        >
                            <option value="performance">Performance report</option>
                            <option value="alert">Alert report</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group controlId="reportTitle" className="mt-3">
                        <Form.Label>Report title</Form.Label>
                        <Form.Control
                            type="text"
                            value={reportTitle}
                            onChange={(e) => setReportTitle(e.target.value)}
                            placeholder="Enter report title (optional)"
                        />
                    </Form.Group>

                    <Form.Group className="mt-3">
                        <Form.Label htmlFor="download-report-time-zone">Time zone</Form.Label>
                        <Select
                            inputId="download-report-time-zone"
                            options={timeZoneOptions}
                            value={timeZoneOptions.find((option) => option.value === timeZone) || { value: timeZone, label: timeZone }}
                            onChange={(option) => setTimeZone(option?.value || resolveScheduledReportTimeZone(undefined, true))}
                            isSearchable
                        />
                        <Form.Text className="text-muted">
                            Controls the report's calendar days and PDF display. Your browser time zone is selected by default.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mt-3">
                        <Form.Label>Reporting period</Form.Label>
                        <div className="report-date-selector report-download-date-selector">
                            <DateRangeSelector
                                pageKey="download-report"
                                defaultLabel="This Month"
                                submitInitialRange
                                compactMenu
                                onSubmit={async (startDate, endDate) => {
                                    setFromDate(format(startDate, 'yyyy-MM-dd'))
                                    setToDate(format(endDate, 'yyyy-MM-dd'))
                                }}
                            />
                        </div>
                        <Form.Text className="text-muted">
                            {fromDate && toDate
                                ? `Selected: ${fromDate} to ${toDate}`
                                : 'Choose and apply a date range before downloading.'}
                        </Form.Text>
                    </Form.Group>

                    {reportType === 'performance' && (
                        <>
                            <Form.Group className="mt-3">
                                <Form.Text className="d-block text-muted mb-2">
                                    Automatically grouped by: {performanceLabels.name}
                                </Form.Text>
                                <AdditionalReportPages
                                    idPrefix="download"
                                    granularity={performanceGranularity}
                                    userKind={userKind}
                                    value={reportSections}
                                    onChange={setReportSections}
                                />
                            </Form.Group>
                            <div className="mt-3 pt-3 border-top">
                                <Form.Label>Data selection</Form.Label>
                            </div>
                            <FiltersGrid
                                opts={{ campaigns, countries, networks, publishers, channels }}
                                selectedFilters={filters}
                                allCollapsed
                                onItemSelect={(type, item) => {
                                    setFilters((prev) => {
                                        const current = prev[type]
                                        const isSelected =
                                            typeof item === 'string'
                                                ? (current as string[]).includes(item)
                                                : (current as PublisherOrCampaign[]).some(
                                                    (i) =>
                                                        i.id === item.id &&
                                                        i.network === item.network &&
                                                        i.country === item.country
                                                )

                                        const updated = isSelected
                                            ? (current as any[]).filter((i) =>
                                                typeof item === 'string'
                                                    ? i !== item
                                                    : i.id !== item.id || i.network !== item.network || i.country !== item.country
                                            )
                                            : [...current, item]

                                        return {
                                            ...prev,
                                            [type]: updated,
                                        }
                                    })
                                }}
                                onSelectAll={handleSelectAll}
                            />
                        </>
                    )}

                    {reportType === 'alert' && (
                        <Form.Group className="mt-3">
                            <Form.Label>Alerts</Form.Label>
                                {allAlerts.map(alert => (
                                    <Form.Check
                                        key={alert.id}
                                        type="checkbox"
                                        id={`alert-${alert.id}`} // 🔹 unique ID
                                        checked={selectedAlertIds.includes(alert.id)}
                                        onChange={() => handleAlertToggle(alert.id)}
                                        className="mb-1"
                                        label={
                                            <Form.Label htmlFor={`alert-${alert.id}`} className="mb-0">
                                                {alert.name}
                                            </Form.Label>
                                        }
                                    />
                                ))}   
                        </Form.Group>
                    )}
                </Form>

                <div className="mt-3">
                    <small className="text-muted">
                        Generating a report may take up to 30 seconds.
                    </small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isDownloading}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleDownload} disabled={isDownloading || !fromDate || !toDate}>
                    {isDownloading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Downloading...
                        </>
                    ) : (
                        'Download'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default DownloadReportModal
